/**
 * WHAT COUNTRY IS THIS? — the question, answered once.
 *
 * `salon.country` is FREE TEXT. The admin panel, onboarding and the seed
 * scripts have each written it their own way over the years, and production's
 * dk-store holds the full English NAME, "Israel". Every screen that answered
 * "is this Israel?" by writing the comparison out by hand —
 *
 *     (salon.country || '').toUpperCase() === 'IL'
 *
 * — got 'ISRAEL' !== 'IL' and quietly decided NO. One stored spelling produced
 * eight separate production faults: an Israeli business lost the POS entry, the
 * customer-payments tab and the wallet from its own sidebar, was quoted its
 * prices in dollars, had its message-language options collapsed to English, and
 * printed packing slips in the wrong language. Nothing errored anywhere.
 *
 * The class survived being fixed three times — server, storefront, portal card
 * util — because the comparison had been written out by hand in a dozen files.
 * So it is written here, once, and nowhere else. A guard
 * (`everyCountryComparisonGoesThroughTheUtil`) fails the build if a country is
 * compared to a quoted literal anywhere in either app outside this module.
 *
 * IDENTITY ONLY — THIS GRANTS NOTHING. `countryCodeOf` answers "which country",
 * never "may it do X". Permission layers on top: `cardAvailability` resolves a
 * code through here and then checks its OWN allow-list, so adding a spelling
 * can never open a rail, and only editing CARD_ENABLED_COUNTRIES can. Keeping
 * the two apart is the point — a general country-name mapper wired into a gate
 * would be the worse bug, silently opening Jordan the day its name resolved.
 *
 * Mirrored byte-for-byte in the portal and the storefront (each app is its own
 * CRA build with its own node_modules, so neither can import the other's
 * utils). The guard asserts the two copies are identical, so they cannot drift.
 */

/**
 * How Israel is written in the salon rows we actually have: the ISO code, its
 * alpha-3, and the name in the three languages Stylon ships. Keys are compared
 * upper-cased with whitespace collapsed; Hebrew and Arabic have no case.
 *
 * Every value is a CODE. A spelling is a way of saying a country, never a way
 * of enabling one.
 */
export const ISRAEL_SPELLINGS = {
  IL: 'IL',
  ISR: 'IL',
  ISRAEL: 'IL',
  ישראל: 'IL',
  إسرائيل: 'IL',
  اسرائيل: 'IL',
};

/**
 * The ISO code a stored country value means, or the value itself (upper-cased)
 * when we do not recognise it — never a guess. Empty/absent resolves to '' and
 * the caller decides what that means: the card utils read it as a legacy
 * Israeli salon, the currency table as "use the legacy default".
 *
 * A name is never sliced to two letters: "Israel".slice(0, 2) is 'IS', which is
 * Iceland. That trap is why this is an explicit table rather than a prefix rule.
 */
export const countryCodeOf = (stored) => {
  const key = String(stored ?? '').replace(/\s+/g, ' ').trim().toUpperCase();
  if (!key) return '';
  return Object.prototype.hasOwnProperty.call(ISRAEL_SPELLINGS, key)
    ? ISRAEL_SPELLINGS[key]
    : key;
};

/**
 * Whether a stored country value IS the given country, however either was
 * written. Both sides are resolved, so a row holding "Israel" matches 'IL'.
 * An empty code asks nothing and matches nothing.
 */
export const isCountry = (stored, code) => {
  const want = countryCodeOf(code);
  if (!want) return false;
  return countryCodeOf(stored) === want;
};
